import * as React from 'react';

import { Unit, Weight } from '@rematter/frontend-lib-api-client';
import { cn, Col, Row, Widget } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import { ItemsHeader } from './_components/ItemsHeader';
import { ItemsList } from './_components/ItemsList';
import { ItemsSummaryFooter } from './_components/ItemsSummaryFooter';
import {
  $itemsInterfaceInventoryLocked,
  $itemsInterfaceTicketId,
  $newlyAddedItemId,
  $resetItemsInterfaceTicketId,
  $syncItemsInterfaceConfig,
  DeductionChangeCallbackFn,
  SuggestedItemPrice,
} from './_state';
import { ItemsListEntry } from './_types';
import { EditMaterialDetailsDialog } from './dialogs/EditMaterialDetailsDialog';
import { AddItemsWidget } from './widgets/AddItemsWidget';
import { ScaleAndCameraWidget } from './widgets/ScaleAndCameraWidget';

import { $sidebarCollapsed } from '~/app/state/sidebar.state';
import { WeightUnitType } from '~/modules/trade/types/load';

type ItemsInterfaceProps = {
  entries?: ItemsListEntry[];
  onUpdateEntries?: (entries: ItemsListEntry[]) => void;
  showPricing?: boolean;
  disabled?: boolean;
  inventoryLocked?: boolean;
  facilityDefaultUnit?: Unit;
  weightUnitMode?: WeightUnitType;
  ticketId?: string | null;
  ticketStatus?: string | null;
  heavyWeight?: Weight | null;
  lightWeight?: Weight | null;
  sidebarContent?: React.ReactNode;
  onDeductionChange?: DeductionChangeCallbackFn;
  highlightedMaterialIds?: string[];
  highlightedSectionTitle?: string;
  suggestedPrices?: Map<string, SuggestedItemPrice>;
};

//
// ⚛️ ⚛️ ⚛️ ITEMS INTERFACE ⚛️ ⚛️ ⚛️
//
export const ItemsInterface: React.FC<ItemsInterfaceProps> = ({
  entries,
  onUpdateEntries,
  showPricing = false,
  disabled = false,
  inventoryLocked = false,
  facilityDefaultUnit,
  weightUnitMode = 'scale',
  ticketId = null,
  ticketStatus = null,
  heavyWeight,
  lightWeight,
  sidebarContent,
  onDeductionChange,
  highlightedMaterialIds,
  highlightedSectionTitle,
  suggestedPrices,
}) => {
  // States
  const [newlyAddedItemId, setNewlyAddedItemId] = React.useState<string | null>(
    null,
  );
  // Atoms
  const atomInventoryLocked = useAtomValue($itemsInterfaceInventoryLocked);
  const atomTicketId = useAtomValue($itemsInterfaceTicketId);
  const atomNewlyAddedItemId = useAtomValue($newlyAddedItemId);
  const syncConfig = useSetAtom($syncItemsInterfaceConfig);
  const resetTicketId = useSetAtom($resetItemsInterfaceTicketId);
  const sidebarCollapsed = useAtomValue($sidebarCollapsed);

  // Effects
  /** Sync external props into the items interface config atom */
  React.useLayoutEffect(() => {
    syncConfig({
      entries: entries ?? [],
      showPricing,
      disabled,
      inventoryLocked,
      facilityDefaultUnit: facilityDefaultUnit ?? ('Pounds' as Unit),
      weightUnitMode,
      ticketId,
      ticketStatus,
      heavyWeight,
      lightWeight,
      onUpdateEntries,
      onDeductionChange,
      highlightedMaterialIds,
      highlightedSectionTitle,
      suggestedPrices,
    });
  }, [
    entries,
    showPricing,
    disabled,
    inventoryLocked,
    facilityDefaultUnit,
    weightUnitMode,
    ticketId,
    ticketStatus,
    heavyWeight,
    lightWeight,
    syncConfig,
    onUpdateEntries,
    onDeductionChange,
    highlightedMaterialIds,
    highlightedSectionTitle,
    suggestedPrices,
  ]);

  React.useEffect(() => {
    resetTicketId();
  }, [resetTicketId]);

  /** Capture newly added item id from the atom into local state */
  React.useEffect(() => {
    if (atomNewlyAddedItemId) {
      setNewlyAddedItemId(atomNewlyAddedItemId);
    }
  }, [atomNewlyAddedItemId]);

  /** Clear newly added item highlight after a short delay */
  React.useEffect(() => {
    if (newlyAddedItemId) {
      const timeout = setTimeout(() => {
        setNewlyAddedItemId(null);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [newlyAddedItemId]);

  return (
    <div className="@ItemsInterfaceContent h-full min-h-0 min-w-0 flex-1">
      <Row className="h-full w-full" gap1>
        <Col className="h-full min-h-0 min-w-0 flex-1" gap1>
          <Widget
            className={cn(
              '@ItemsWidget',
              'flex min-h-0 w-full flex-1 flex-col',
            )}
          >
            <div className="px-3 pt-2">
              <ItemsHeader />
            </div>
            <div
              className={
                'no-scrollbar h-full overflow-y-auto rounded-lg px-3 py-2 outline-2 outline-white'
              }
            >
              <ItemsList newlyAddedItemId={newlyAddedItemId} />
            </div>
          </Widget>

          {!atomInventoryLocked && <AddItemsWidget />}
          {atomTicketId && <ItemsSummaryFooter />}
          <EditMaterialDetailsDialog />
        </Col>

        {!atomInventoryLocked && (
          <Col
            className={cn(
              'h-full w-[190px] min-w-0',
              sidebarCollapsed && '2xl:w-[250px]',
            )}
            gap1
          >
            <ScaleAndCameraWidget />
            {sidebarContent}
          </Col>
        )}
      </Row>
    </div>
  );
};
